import React, { useEffect } from 'react';
import { ActionIcon, Button, Flex, Paper, ScrollArea, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { IconArrowRight, IconDownload, IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../global/style/theme.tsx';
import { useElementSize } from '@mantine/hooks';
import { useGetDocuments } from '../../../global/entities/Document/queries/useGetDocuments.ts';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { notifications } from '@mantine/notifications';
import { DocumentApi } from '../../../global/apis/Documents/DocumentApi.ts';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { modals } from '@mantine/modals';
import { useDeleteDocumentMutations } from '../../../global/entities/Document/mutations/useDeleteDocumentMutations.tsx';

interface IDocumentsProps {
  patientStore?: PatientStore;
}

function formatSize(size: number): string {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${size} B`;
}

const DocumentsTable: React.FC<IDocumentsProps> = inject('patientStore')(
  observer(({ patientStore }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ref, width } = useElementSize();
    const { currentUser } = useSessionProvider();
    
    const { data: documents = [], isLoading } = useGetDocuments(patientStore?.patientId || currentUser?.patientID);
    const { mutate: deleteDocument } = useDeleteDocumentMutations();
    const { setSortedData, sortedData, sortBy, reverseSortDirection, setSorting } = useTable();

    useEffect(() => {
      // Only update if the arrays are different in length or any id is different
      const needsUpdate =
        sortedData.length !== documents.length ||
        sortedData.some((item, i) => item.id !== documents[i]?.id);

      if (needsUpdate) 
        setSortedData(documents);
      
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents, setSortedData]);

    const handleSort = (column: string) => {
      setSorting(column, sortedData);
    };

    const handleDownload = async (docId: string, name: string) => {
      try {
        const url = await DocumentApi.getDownloadUrl(patientStore?.patientId || currentUser?.patientID || '', docId, encodeURIComponent(name));
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch {
        notifications.show({ color: 'red', message: t('notifications.downloadFailed') });
      }
    };

    const handleDelete = async (docId: string) => {
      try {
        await deleteDocument({ id: docId });
        notifications.show({ color: 'green', message: t('notifications.deleteSuccess') });
      } catch {
        notifications.show({ color: 'red', message: t('notifications.deleteFailed') });
      }
    };

    const handLeDocuments = (id?: number) => {
      navigate(`documents/${id}`);
    };

    const confirmDelete = (docId: string) => {
      modals.openConfirmModal({
        title: t('modal.deleteDocumentModal.title'),
        children: (
            <Stack gap={'sm'}>
                <Text>{t('modal.deleteDocumentModal.question')}</Text>
                <Text fw={'bold'}> {t('modal.deleteDocumentModal.details')}</Text>
            </Stack>
        ),
        labels: {
            confirm: t('modal.deleteDocumentModal.yes'),
            cancel: t('modal.deleteDocumentModal.no'),
        },
        confirmProps: { color: 'red' },
        onConfirm: () => handleDelete(docId),
        size: 'lg',
        radius: 'xs',
      });
    };

    return (
      <Paper h={'100%'} w={'100%'} ref={ref}>
        <Stack h={'100%'}>
          <Flex justify={'space-between'} align="flex-start" gap="md">
            <Stack gap={4} align="flex-start" flex={1}>
              <Title order={4}>{t('table.documents.documents')}</Title>
              <Text size="sm" c="dimmed">
                {t('table.documents.documentsSubLabel')}
              </Text>
            </Stack>
            <Button
              hidden={width <= 500}
              variant="filled"
              leftSection={<IconPlus size={14} />}
              onClick={() => handLeDocuments(0)}
              style={{ flexShrink: 0 }}
            >
              {t('table.documents.addDocument')}
            </Button>
          </Flex>
          <ScrollArea w="100%">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Th
                    sorted={sortBy === 'name'}
                    reversed={reverseSortDirection}
                    onSort={() => handleSort('name')}
                  >
                    {t('table.documents.documentName')}
                  </Th>
                  <Th
                    sorted={sortBy === 'addedBy'}
                    reversed={reverseSortDirection}
                    onSort={() => handleSort('addedBy')}
                  >
                    {t('table.documents.addedBy')}
                  </Th>
                  <Th
                    sorted={sortBy === 'filename'}
                    reversed={reverseSortDirection}
                    onSort={() => handleSort('filename')}
                  >
                    {t('table.documents.filename')}
                  </Th>
                  <Th
                    sorted={sortBy === 'dateAdded'}
                    reversed={reverseSortDirection}
                    onSort={() => handleSort('dateAdded')}
                  >
                    <Stack gap={0}>
                      {t('global.date')}
                      <Text size={'xs'} c={'dimmed'}>YYYY-MM-DD</Text>
                    </Stack>
                  </Th>
                  <Th
                    sorted={sortBy === 'size'}
                    reversed={reverseSortDirection}
                    onSort={() => handleSort('size')}
                  >
                    {t('table.documents.size')}
                  </Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {isLoading ? (
                  <Table.Tr><Table.Td colSpan={5}>{t('global.loading')}</Table.Td></Table.Tr>
                ) : sortedData.map((row, index) => {
                  return (
                    <Table.Tr key={row.id} style={{ background: index % 2 === 1 ? 'var(--Background-Gray-light, #E9ECEF)' : undefined }}>
                      <Table.Td>
                        <Text style={{ fontSize: 14, fontWeight: 500 }} truncate>
                          {row.name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text style={{ fontSize: 14, fontWeight: 500 }} truncate>
                          {row.addedBy}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text style={{ fontSize: 14, fontWeight: 500 }} truncate>
                          {row.filename}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text style={{ fontSize: 14, fontWeight: 500 }} truncate>
                          {dayjs(row.dateAdded).format('YYYY-MM-DD')}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text style={{ fontSize: 14, fontWeight: 500 }} truncate>
                          {formatSize(row.size)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Flex align={'center'}>
                          <Tooltip label={t('toolTip.download')}>
                            <ActionIcon variant="subtle" component={'button'} color={'blue'} onClick={() => handleDownload(row.id, row.filename)}><IconDownload size={18} /></ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('toolTip.delete')}>
                            <ActionIcon variant="subtle" color={'red'} onClick={() => confirmDelete(row.id)}><IconTrash size={18} /></ActionIcon>
                          </Tooltip>
                          <ActionIcon onClick={() => handLeDocuments(row.id)} variant="transparent"><IconArrowRight color={theme.colors?.blue?.toString()} /></ActionIcon>
                        </Flex>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          <Button
            hidden={width > 500}
            fullWidth
            mt={'lg'}
            variant="filled"
            leftSection={<IconPlus size={14} />}
            onClick={() => handLeDocuments(0)}
          >{t('table.documents.addDocument')}</Button>
        </Stack>
      </Paper>
    );
  })
);

const WrappedDocumentsTable: React.FC = () => {
  return (
    <TableContextProvider>
      <DocumentsTable />
    </TableContextProvider>
  );
};

export default WrappedDocumentsTable;
